import React from 'react';
import styles from './agencyDetail.module.scss';
import { Nav } from 'react-bootstrap';
import Basic from 'components/AgencyDetail/Basic/Basic';
import Addon from 'components/AgencyDetail/Addon/Addon';
import { AgencyMemberTab } from './AgencyMemberTab';
import {
  AgencyDetailModel,
  AgencyDetailProps,
  AgencyDetailState
} from './AgencyDetailModel';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import i18n from 'i18next';
import { hasFuncs, notRoles } from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import { AdLogoType } from 'core/adLogo/AdLogo';
import defaultAdLogo from 'assets/campaign/tenmax-ad-logo.svg';
import cx from 'classnames/bind';
import { RoleNames } from 'core';
import { TitleWithBreadcrumbs } from 'components/TitleArea/TitleWithBreadcrumbs';
import { DynamicBreadcrumb } from 'components/Breadcrumbs/DynamicBreadcrumbs';
import { AuditLog } from 'containers/AuditLog/AuditLog';
import { InfoDisplaySectionTitle } from 'components/InfoDisplaySection/InfoDisplaySection';
import { Modal } from 'components/common/Modal/Modal';
import { AgencyStatus } from 'core/agency/Agency';
import { AgencyDetailModalData } from './AgencyDetailModel';

const classNames = cx.bind(styles);

export class AgencyDetail extends React.Component<
  AgencyDetailProps,
  AgencyDetailState
> {
  model: AgencyDetailModel;
  handler: number;
  constructor(props: any) {
    super(props);
    this.model = this.props.model;
    this.state = this.model.state;
    this.handler = this.model.event.add((model) => {
      this.setState(model.state);
    });
  }

  componentDidMount() {
    this.model.init();
  }

  componentDidUpdate(prevProps: AgencyDetailProps) {
    if (prevProps.model !== this.props.model) {
      prevProps.model.event.remove(this.handler);
      this.handler = this.props.model.event.add((model) => {
        this.setState(model.state);
      });
      this.model = this.props.model;
      this.props.model.init();
    }
  }

  componentWillUnmount() {
    this.props.model.event.remove(this.handler);
  }

  renderBasicInfo = () => {
    const { defaultAgency, isSystemViewOnly } = this.props.model;
    if (!defaultAgency) {
      return <div />;
    }
    return (
      <Basic agencyDetail={defaultAgency} isSystemViewOnly={isSystemViewOnly} />
    );
  };

  renderAddonInfo = () => {
    if (!this.props.model.defaultAgency) {
      return <div />;
    }
    return <Addon agencyDetail={this.props.model.defaultAgency} />;
  };

  renderAdLogo = () => {
    if (!this.props.model.defaultAgency) {
      return <div />;
    }
    const adLogo = this.props.model.defaultAgency.adLogo;
    let imageData;
    let link;
    if (adLogo.type !== AdLogoType.NULL) {
      imageData = adLogo.image;
      link = adLogo.link;
      if (adLogo.type === AdLogoType.DEFAULT) {
        imageData = { url: defaultAdLogo };
        link = 'https://www.tenmax.io/tw/';
      }
    }
    const openLink = () => {
      link && window.open(link, '_blank', 'noopener');
    };

    const adLogoDetailClass = classNames('adLogoDetail', {
      clickable: imageData && link
    });
    return (
      <div className={adLogoDetailClass}>
        {imageData ? (
          <img
            src={imageData.url}
            alt={i18n.t('common.labels.noData')}
            onClick={openLink}
          />
        ) : (
          i18n.t('common.labels.noData')
        )}
      </div>
    );
  };

  renderAuditLog = () => {
    const auditLogModel = this.props.model.getAuditLogModel();
    if (!auditLogModel) {
      return <div />;
    }
    return <AuditLog model={auditLogModel} />;
  };

  renderAdDeclaration = (adDeclarationUrl?: string) => {
    const fileName = adDeclarationUrl?.split('/').pop() || adDeclarationUrl;
    return (
      <div className={styles.adDeclarationDetail}>
        <InfoDisplaySectionTitle
          title={i18n.t('agencyDetail.labels.adDeclarationTab')}
        />
        {fileName ? (
          <div className={styles.adDeclarationLink}>
            <a
              href={adDeclarationUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              {fileName}
            </a>
          </div>
        ) : (
          'N/A'
        )}
      </div>
    );
  };

  renderArchiveModal = (modalData: AgencyDetailModalData) => {
    const model = this.props.model;
    return (
      <Modal
        fullScreen={false}
        title={modalData.title}
        primaryButton={{
          title: modalData.confirmBtnData.title,
          callback: modalData.confirmBtnData.callback,
          disabled: this.state.isMutating
        }}
        secondaryButton={{
          title: i18n.t('common.buttons.cancel'),
          callback: model.hideModal,
          disabled: this.state.isMutating
        }}
        dismiss={model.hideModal}
      >
        {modalData.message}
      </Modal>
    );
  };

  renderArchiveButton = () => {
    const isArchived = this.state.status === AgencyStatus.ARCHIVED;
    return (
      <PermissionChecker
        permissionAware={hasFuncs(Permission.SYS_ADMIN_MANAGEMENT)}
      >
        <a
          onClick={
            isArchived
              ? this.props.model.restoreAgency
              : this.props.model.showArchiveConfirmModal
          }
        >
          {isArchived
            ? i18n.t('common.buttons.restore')
            : i18n.t('common.buttons.archive')}
        </a>
      </PermissionChecker>
    );
  };

  renderTitleWithBreadcrumbs = () => {
    return (
      <TitleWithBreadcrumbs
        title={this.props.model.title}
        routes={[
          {
            path: '/agencies',
            breadcrumb: i18n.t('agencies.home.title')
          },
          {
            path: '/agencies/:agencyId',
            breadcrumb: DynamicBreadcrumb,
            props: {
              label: _.get(this.props.model.defaultAgency, 'companyName'),
              matchParam: 'agencyId'
            }
          }
        ]}
      />
    );
  };

  render() {
    const { defaultAgency, showTab } = this.props.model;
    if (!defaultAgency) {
      return <LoadingIndicator />;
    }
    const { isLoading } = this.state;
    if (isLoading) {
      return <LoadingIndicator />;
    }
    const hasExpressPass = !_.isEmpty(
      _.get(defaultAgency, 'addonProps.expressPass', {})
    );
    const adDeclarationUrl = _.get(defaultAgency, 'adDeclaration.url');
    return (
      <div className={styles.container}>
        {this.state.isMutating && <LoadingIndicator />}
        {this.state.modalData && this.renderArchiveModal(this.state.modalData)}
        <PermissionChecker
          permissionAware={notRoles(RoleNames.sysAdmin)}
          renderOtherwise={this.renderTitleWithBreadcrumbs}
        >
          <header>
            <h3>{this.props.model.title}</h3>
          </header>
        </PermissionChecker>
        <div className={styles.titleBottomLine} />
        <div className={styles.agencyDetail}>
          <div className={styles.topArea}>
            <div className={styles.title}>
              {`${i18n.t('agency.companyName')}: ${defaultAgency.companyName}`}
            </div>
            <div className={styles.buttonArea}>
              <PermissionChecker
                permissionAware={hasFuncs(Permission.AGENCY_UPDATE)}
              >
                <Link to={`${defaultAgency.id}/edit`}>
                  {i18n.t('agencyDetail.labels.editAgency')}
                </Link>
              </PermissionChecker>
              {this.renderArchiveButton()}
            </div>
          </div>
          <PermissionChecker
            permissionAware={notRoles(
              RoleNames.beaconAdmin,
              RoleNames.beaconExecutive
            )}
          >
            <div className={styles.infoArea}>
              <Nav activeKey={this.state.dataTabName} onSelect={showTab}>
                <Nav.Item>
                  <Nav.Link eventKey='basic'>
                    {i18n.t('agencyDetail.labels.basicTab')}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey='addon'>
                    {i18n.t('agencyDetail.labels.addonFeatureTab')}
                  </Nav.Link>
                </Nav.Item>
                {_.get(
                  defaultAgency,
                  'addonFeatures.creatives.customizeAdLogo'
                ) && (
                  <Nav.Item>
                    <Nav.Link eventKey='adLogo'>
                      {i18n.t('agencyDetail.labels.adLogoTab')}
                    </Nav.Link>
                  </Nav.Item>
                )}
                <PermissionChecker
                  permissionAware={hasFuncs(Permission.SYS_ADMIN_MANAGEMENT)}
                >
                  <Nav.Item>
                    <Nav.Link eventKey='auditLog'>
                      {i18n.t('agencyDetail.labels.auditLogTab')}
                    </Nav.Link>
                  </Nav.Item>
                </PermissionChecker>
                {hasExpressPass && (
                  <Nav.Item>
                    <Nav.Link eventKey='adDeclaration'>
                      {i18n.t('agencyDetail.labels.adDeclarationTab')}
                    </Nav.Link>
                  </Nav.Item>
                )}
              </Nav>
              {this.state.dataTabName === 'basic' && this.renderBasicInfo()}
              {this.state.dataTabName === 'addon' && this.renderAddonInfo()}
              {this.state.dataTabName === 'adLogo' && this.renderAdLogo()}
              {this.state.dataTabName === 'auditLog' && this.renderAuditLog()}
              {this.state.dataTabName === 'adDeclaration' &&
                hasExpressPass &&
                this.renderAdDeclaration(adDeclarationUrl)}
            </div>
          </PermissionChecker>
        </div>
        <div className={styles.memberList}>
          <PermissionChecker
            permissionAware={hasFuncs(Permission.AGENCY_MEMBER_READ)}
          >
            <AgencyMemberTab model={this.model.getAgencyMemberTabModel()} />
          </PermissionChecker>
        </div>
      </div>
    );
  }
}
